import classNames from 'classnames'
import { Transition, Variants, motion } from 'framer-motion'
import React, { useCallback, useEffect, useMemo } from 'react'

import { ModalInstanceProvider, useModalInstance } from '@context/ModalInstanceContext'
import { ModalData } from '@context/ModalsContext'

import { useWindowResize } from '@utils/domUtils'

import styles from './ModalWrapper.module.scss'

const overlayVariants: Variants = {
  active: { opacity: 1 },
  inactive: { opacity: 0 },
}

const overlayTransition: Transition = { duration: 0.22, ease: [0.2, 0.0, 0.2, 1] }
const contentVariants: Variants = {
  active: {
    opacity: 1,
    y: 0,
    scale: 1,
  },
  inactive: {
    opacity: 0,
    y: 16,
    scale: 0.98,
  },
}

const contentTransition: Transition = {
  duration: 0.22,
  ease: [0.2, 0.0, 0.2, 1],
}

interface Props {
  modals: ModalData[]
}

export const ModalWrapper: React.FC<Props> = ({ modals }) => {
  const interactiveIndex = modals.length - 1

  return (
    <div className={styles.container}>
      {modals.map((modal, index) => (
        <ModalInstanceProvider
          key={modal.id}
          value={{
            id: modal.id,
            close: modal.close,
            isClosing: !!modal.isClosing,
            isInteractive: index === interactiveIndex,
          }}
        >
          <ModalStage modal={modal} isInteractive={index === interactiveIndex} />
        </ModalInstanceProvider>
      ))}
    </div>
  )
}

interface ModalStageProps {
  modal: ModalData
  isInteractive: boolean
}

const ModalStage: React.FC<ModalStageProps> = ({ modal, isInteractive }) => {
  const liveProps = useMemo(() => modal.props ?? {}, [modal.props])
  const { canClose, containerMode } = useModalInstance()
  const isMobile = useWindowResize()
  const modalClose = modal.close
  const isContentVisible = isInteractive || !!modal.isClosing
  const canAnimatePositioner = containerMode === 'popup'
  const positionerAnimation = canAnimatePositioner
    ? isContentVisible
      ? 'active'
      : 'inactive'
    : { opacity: 1 }

  const resolvedContainerMode = containerMode ?? modal.component.containerMode
  const isDrawer = resolvedContainerMode === 'drawer'
  const canOverlayClose = isInteractive && canClose && resolvedContainerMode !== 'bottomSheet'
  const isOverlayTransparent = isDrawer && !isMobile
  const contentPointerEvents = isInteractive && !modal.isClosing ? 'auto' : 'none'

  const handleClose = useCallback(() => {
    void modalClose()
  }, [modalClose])

  useEffect(() => {
    if (!isInteractive || !canClose) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose()
      }

      if (e.key === 'Enter') {
        const target = e.target as HTMLElement
        const modalPositioner = document.querySelector(`.${styles.modalPositioner}`)
        if (modalPositioner && !modalPositioner.contains(target)) {
          e.preventDefault()
          e.stopPropagation()
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isInteractive, canClose, handleClose])

  const content = (
    <modal.component
      id={modal.id}
      close={modal.close}
      onClose={() => undefined}
      {...(liveProps as Record<string, unknown>)}
    />
  )

  return (
    <div
      className={classNames(styles.stage, {
        [styles.stageDrawer]: isDrawer && !isMobile,
      })}
    >
      <motion.div
        className={classNames(styles.overlay, {
          [styles.overlayTransparent]: isOverlayTransparent,
        })}
        variants={overlayVariants}
        initial='inactive'
        animate={isInteractive && !modal.isClosing ? 'active' : 'inactive'}
        transition={overlayTransition}
        style={{
          pointerEvents: canOverlayClose ? 'auto' : 'none',
        }}
        onClick={canOverlayClose ? handleClose : undefined}
      />

      {isDrawer ? (
        <div style={{ pointerEvents: contentPointerEvents }}>{content}</div>
      ) : (
        <motion.div
          className={styles.modalPositioner}
          variants={canAnimatePositioner ? contentVariants : undefined}
          initial={false}
          animate={positionerAnimation}
          transition={canAnimatePositioner ? contentTransition : undefined}
          transformTemplate={canAnimatePositioner ? undefined : () => 'none'}
          style={{ pointerEvents: contentPointerEvents }}
        >
          {content}
        </motion.div>
      )}
    </div>
  )
}
